/**
 * The chemical elements and their atomic weights — the data under every chemistry
 * calculator on the site.
 *
 * ## Source
 *
 * Atomic weights are the **abridged standard atomic weights** published by the IUPAC
 * Commission on Isotopic Abundances and Atomic Weights (CIAAW), "Abridged Standard Atomic
 * Weights 2024" (https://ciaaw.org/abridged-atomic-weights.htm), which restates the
 * 2021 technical report (Pure Appl. Chem. 94(5), 573–600, 2022) with the 2024 revisions
 * of gadolinium, lutetium and zirconium. CIAAW quotes them to five significant figures
 * unless the isotopic variability of the element limits the precision, so:
 *
 * - For the twelve elements whose standard atomic weight is an *interval* (H, Li, B, C, N,
 *   O, Mg, Si, S, Cl, Br, Tl), and for argon and lead, the abridged table prints the
 *   conventional value a textbook uses: H 1.008, C 12.011, O 15.999, Cl 35.45, Pb 207.2.
 * - Every other element carries its five-figure value (Cu 63.546, Fe 55.845, Na 22.99).
 *
 * ## Elements with no standard atomic weight
 *
 * Thirty-four elements (Tc, Pm, Po–Ac, Np–Og) have no stable isotopes in a composition
 * that makes a standard atomic weight meaningful, and IUPAC publishes none. For these the
 * table holds the **mass number of the longest-lived known isotope** — the convention of
 * periodic tables that print a bracketed number — and flags the entry `massNumber: true`,
 * so a calculator can say that the figure is an isotope's mass number and not a standard
 * atomic weight.
 *
 * ## Deuterium
 *
 * `D` (²H, 2.0141 u, from the Atomic Mass Evaluation) is accepted in formulas so that
 * D2O parses. It is an isotope label rather than an element and carries `isotope: true`.
 *
 * Later chemistry families import this module; keep the shape stable.
 */

export interface ChemElement {
  /** Atomic number. Deuterium reuses 1. */
  z: number;
  symbol: string;
  name: string;
  /** Atomic weight in g/mol (the mass number of the longest-lived isotope if `massNumber`). */
  weight: number;
  /** True when `weight` is an isotope's mass number, not an IUPAC standard atomic weight. */
  massNumber: boolean;
  /** True for isotope labels such as D. */
  isotope: boolean;
}

/** [Z, symbol, name, weight, weightIsMassNumber] */
type Row = readonly [number, string, string, number, boolean?];

const ROWS: readonly Row[] = [
  [1, "H", "Hydrogen", 1.008],
  [2, "He", "Helium", 4.0026],
  [3, "Li", "Lithium", 6.94],
  [4, "Be", "Beryllium", 9.0122],
  [5, "B", "Boron", 10.81],
  [6, "C", "Carbon", 12.011],
  [7, "N", "Nitrogen", 14.007],
  [8, "O", "Oxygen", 15.999],
  [9, "F", "Fluorine", 18.998],
  [10, "Ne", "Neon", 20.18],
  [11, "Na", "Sodium", 22.99],
  [12, "Mg", "Magnesium", 24.305],
  [13, "Al", "Aluminum", 26.982],
  [14, "Si", "Silicon", 28.085],
  [15, "P", "Phosphorus", 30.974],
  [16, "S", "Sulfur", 32.06],
  [17, "Cl", "Chlorine", 35.45],
  [18, "Ar", "Argon", 39.95],
  [19, "K", "Potassium", 39.098],
  [20, "Ca", "Calcium", 40.078],
  [21, "Sc", "Scandium", 44.956],
  [22, "Ti", "Titanium", 47.867],
  [23, "V", "Vanadium", 50.942],
  [24, "Cr", "Chromium", 51.996],
  [25, "Mn", "Manganese", 54.938],
  [26, "Fe", "Iron", 55.845],
  [27, "Co", "Cobalt", 58.933],
  [28, "Ni", "Nickel", 58.693],
  [29, "Cu", "Copper", 63.546],
  [30, "Zn", "Zinc", 65.38],
  [31, "Ga", "Gallium", 69.723],
  [32, "Ge", "Germanium", 72.63],
  [33, "As", "Arsenic", 74.922],
  [34, "Se", "Selenium", 78.971],
  [35, "Br", "Bromine", 79.904],
  [36, "Kr", "Krypton", 83.798],
  [37, "Rb", "Rubidium", 85.468],
  [38, "Sr", "Strontium", 87.62],
  [39, "Y", "Yttrium", 88.906],
  [40, "Zr", "Zirconium", 91.222],
  [41, "Nb", "Niobium", 92.906],
  [42, "Mo", "Molybdenum", 95.95],
  [43, "Tc", "Technetium", 98, true],
  [44, "Ru", "Ruthenium", 101.07],
  [45, "Rh", "Rhodium", 102.91],
  [46, "Pd", "Palladium", 106.42],
  [47, "Ag", "Silver", 107.87],
  [48, "Cd", "Cadmium", 112.41],
  [49, "In", "Indium", 114.82],
  [50, "Sn", "Tin", 118.71],
  [51, "Sb", "Antimony", 121.76],
  [52, "Te", "Tellurium", 127.6],
  [53, "I", "Iodine", 126.9],
  [54, "Xe", "Xenon", 131.29],
  [55, "Cs", "Cesium", 132.91],
  [56, "Ba", "Barium", 137.33],
  [57, "La", "Lanthanum", 138.91],
  [58, "Ce", "Cerium", 140.12],
  [59, "Pr", "Praseodymium", 140.91],
  [60, "Nd", "Neodymium", 144.24],
  [61, "Pm", "Promethium", 145, true],
  [62, "Sm", "Samarium", 150.36],
  [63, "Eu", "Europium", 151.96],
  [64, "Gd", "Gadolinium", 157.25],
  [65, "Tb", "Terbium", 158.93],
  [66, "Dy", "Dysprosium", 162.5],
  [67, "Ho", "Holmium", 164.93],
  [68, "Er", "Erbium", 167.26],
  [69, "Tm", "Thulium", 168.93],
  [70, "Yb", "Ytterbium", 173.05],
  [71, "Lu", "Lutetium", 174.97],
  [72, "Hf", "Hafnium", 178.49],
  [73, "Ta", "Tantalum", 180.95],
  [74, "W", "Tungsten", 183.84],
  [75, "Re", "Rhenium", 186.21],
  [76, "Os", "Osmium", 190.23],
  [77, "Ir", "Iridium", 192.22],
  [78, "Pt", "Platinum", 195.08],
  [79, "Au", "Gold", 196.97],
  [80, "Hg", "Mercury", 200.59],
  [81, "Tl", "Thallium", 204.38],
  [82, "Pb", "Lead", 207.2],
  [83, "Bi", "Bismuth", 208.98],
  [84, "Po", "Polonium", 209, true],
  [85, "At", "Astatine", 210, true],
  [86, "Rn", "Radon", 222, true],
  [87, "Fr", "Francium", 223, true],
  [88, "Ra", "Radium", 226, true],
  [89, "Ac", "Actinium", 227, true],
  [90, "Th", "Thorium", 232.04],
  [91, "Pa", "Protactinium", 231.04],
  [92, "U", "Uranium", 238.03],
  [93, "Np", "Neptunium", 237, true],
  [94, "Pu", "Plutonium", 244, true],
  [95, "Am", "Americium", 243, true],
  [96, "Cm", "Curium", 247, true],
  [97, "Bk", "Berkelium", 247, true],
  [98, "Cf", "Californium", 251, true],
  [99, "Es", "Einsteinium", 252, true],
  [100, "Fm", "Fermium", 257, true],
  [101, "Md", "Mendelevium", 258, true],
  [102, "No", "Nobelium", 259, true],
  [103, "Lr", "Lawrencium", 266, true],
  [104, "Rf", "Rutherfordium", 267, true],
  [105, "Db", "Dubnium", 268, true],
  [106, "Sg", "Seaborgium", 269, true],
  [107, "Bh", "Bohrium", 270, true],
  [108, "Hs", "Hassium", 269, true],
  [109, "Mt", "Meitnerium", 278, true],
  [110, "Ds", "Darmstadtium", 281, true],
  [111, "Rg", "Roentgenium", 282, true],
  [112, "Cn", "Copernicium", 285, true],
  [113, "Nh", "Nihonium", 286, true],
  [114, "Fl", "Flerovium", 289, true],
  [115, "Mc", "Moscovium", 290, true],
  [116, "Lv", "Livermorium", 293, true],
  [117, "Ts", "Tennessine", 294, true],
  [118, "Og", "Oganesson", 294, true],
];

/** All 118 elements in atomic-number order. */
export const ELEMENTS: readonly ChemElement[] = ROWS.map(
  ([z, symbol, name, weight, massNumber]) => ({
    z,
    symbol,
    name,
    weight,
    massNumber: massNumber === true,
    isotope: false,
  })
);

/** Deuterium, accepted in formulas (D2O). 2.0141 u from the Atomic Mass Evaluation. */
export const DEUTERIUM: ChemElement = {
  z: 1,
  symbol: "D",
  name: "Deuterium",
  weight: 2.0141,
  massNumber: false,
  isotope: true,
};

const BY_SYMBOL = new Map<string, ChemElement>(
  [...ELEMENTS, DEUTERIUM].map((element) => [element.symbol, element])
);

/** The element for a case-sensitive symbol ("Co", not "CO"), or undefined. */
export function getElement(symbol: string): ChemElement | undefined {
  return BY_SYMBOL.get(symbol);
}

/** Avogadro constant, mol⁻¹ — exact since the 2019 SI redefinition (BIPM SI Brochure, 9th ed.). */
export const AVOGADRO = 6.022_140_76e23;

/** The abridged-table edition the weights come from, for the page to state. */
export const ATOMIC_WEIGHTS_EDITION =
  "IUPAC/CIAAW abridged standard atomic weights 2024";
